import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { R } from '@core/utils/r';
import { TicketId } from '@business/entities/SupportTicket';
import { SupportTaskResponse } from '@modules/support/SupportTaskResponse';
import { SupportTaskAPI } from '@business/api/SupportTicket';
import { ClickupAPI } from '@business/api/clickup';
import { ClickupTaskRequest } from '@business/entities/clickup';

export class SupportTaskBloc extends BaseBloc {
  private readonly tasksRepo = new Repository<SupportTaskResponse>({
    getItemId: task => task.id,
  });

  readonly tasks$ = this.tasksRepo.items$.pipe(map(tasks => R.sortBy(tasks, t => t.menuOrder)));
  selectTask = this.tasksRepo.selectItem;

  onReset() {
    this.tasksRepo.reset();
  }

  fetchTasks = (id: TicketId) => {
    return SupportTaskAPI.fetchTasks(id).pipe(this.tasksRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_companies_error') }));
  };

  fetchTask = (id: string) => {
    return SupportTaskAPI.fetchTask(id).pipe(
      this.tasksRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  createTask = (ticketId: TicketId, data: Partial<SupportTaskResponse>, parentStepId?: string) => {
    return SupportTaskAPI.createTask(ticketId, data, parentStepId).pipe(
      this.tasksRepo.ops.reset(),
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  updateTask = (id: string, data: Partial<SupportTaskResponse>) => {
    return SupportTaskAPI.updateTask(id, data).pipe(
      this.tasksRepo.ops.reset(),
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  duplicateTask = (id: string) => {
    return SupportTaskAPI.duplicateTask(id).pipe(
      this.tasksRepo.ops.reset(),
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  deleteTask = (id: string) => {
    return showDeleteConfirmation('Delete Task', 'Do you really want to remove this task?').pipe(
      switchMap(() => SupportTaskAPI.deleteTask(id)),

      this.tasksRepo.ops.reset(),
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  toggleTask = (taskId: string, completeAll?: boolean) => {
    return SupportTaskAPI.toggleTask(taskId, completeAll).pipe(this.tasksRepo.ops.reset());
  };

  updateUsers = (taskId: string, users: string[]) => {
    return SupportTaskAPI.updateUsers(taskId, users).pipe(this.tasksRepo.ops.upsertOne(item => ({ item })));
  };

  sortSteps = (ids: string[], stepId?: string) => {
    return SupportTaskAPI.sortSteps(ids).pipe(
      tap(() => {
        let items = this.tasksRepo.items;
        if (stepId) {
          items = items.find(t => t.id === stepId)?.subSteps || [];
        }
        const newItems = items.map(item => {
          return { ...item, menuOrder: ids.indexOf(item.id) };
        });
        if (stepId) {
          this.tasksRepo.updateOne({
            id: stepId,
            changes: () => ({
              subSteps: newItems,
            }),
          });
        } else {
          this.tasksRepo.reset(newItems);
        }
      }),
    );
  };

  createClickupTask = (request: ClickupTaskRequest, projectId: string, stepId: string) => {
    return ClickupAPI.createTask(request).pipe(
      handleMessage({
        type: requestMessage('sync_task_to_clickup'),
      }),
    );
  };

  unSyncClickupTask = (request: ClickupTaskRequest) => {
    return showDeleteConfirmation('Unsync', 'Do you really want to unsync this task from clickup?').pipe(
      switchMap(() => ClickupAPI.unSync(request)),
      handleMessage({
        type: requestMessage('unsync_task_from_clickup'),
      }),
    );
  };
}
